/**
 * Servicio para gestión de gastos
 * Registra gastos sin inventario asociado
 *
 * NOTA: Este servicio ahora usa el patrón Repository via DatabaseFactory.
 * Esto permite desacoplar la lógica de negocio de la implementación específica
 * de base de datos (Dexie para web, SQLite para mobile).
 */

import { contraparteConValor } from '../contraparte/contraparteSearch';
import {
  Gasto,
  GastoDTO,
  FiltroGastos
} from '../../types/administracion';
import { getRepositories } from '../storage';

/**
 * Servicio de Gastos
 *
 * Usa el repositorio via factory para operaciones de datos.
 */
export class GastoService {
  /**
   * Obtiene el repositorio de gastos
   */
  private static getRepo() {
    const { gasto } = getRepositories();
    return gasto;
  }

  /**
   * Crea un nuevo gasto
   */
  static async create(gastoDTO: GastoDTO & {
    empresaId?: string;
    userId?: string;
  }): Promise<string> {
    return this.registrarGasto(gastoDTO);
  }

  /**
   * Alias para create() - usado por componentes
   */
  static async registrarGasto(gastoDTO: GastoDTO & {
    empresaId?: string;
    userId?: string;
    concepto_id?: string;
  }): Promise<string> {
    try {
      const { profile } = getRepositories();

      // Get empresa_id and user_id from profiles if not provided
      let empresaId = gastoDTO.empresaId;
      let userId = gastoDTO.userId;

      if (!empresaId || !userId) {
        const userInfo = await profile.getCurrentUserInfo();
        empresaId = empresaId || userInfo.empresaId;
        userId = userId || userInfo.userId;
      }

      if (!empresaId) {
        throw new Error('No active empresa found');
      }

      if (!userId) {
        throw new Error('User ID not available');
      }

      const fechaActual = new Date().toISOString();

      const gasto: Omit<Gasto, 'id'> = {
        updated_at: fechaActual,
        fecha_evento: gastoDTO.fecha_evento,
        concepto: gastoDTO.concepto,
        descripcion: gastoDTO.descripcion,
        monto: gastoDTO.monto,
        ubicacion_id: gastoDTO.ubicacion_id,
        categoria: gastoDTO.categoria,
        concepto_id: gastoDTO.concepto_id ?? undefined,
        // Override manual tri-state (fix-3a): viaja SOLO cuando el caller lo
        // manda (el form lo incluye sólo si difiere del derivado del concepto);
        // sin la key queda undefined → NULL en la DB = según concepto.
        ...(gastoDTO.es_engorde !== undefined ? { es_engorde: gastoDTO.es_engorde } : {}),
        // A quién se le pagó (2026-10-18): opcional, sólo viaja con valor.
        ...contraparteConValor(gastoDTO),
        empresa_id: empresaId,
        user_id: userId,
        is_deleted: false
      };

      // Guardar usando repositorio
      const gastoId = await this.getRepo().add(gasto);

      return gastoId;
    } catch (error) {
      console.error('Error creando gasto:', error);
      throw error;
    }
  }

  /**
   * Obtiene todos los gastos de la empresa activa
   */
  static async getAll(): Promise<Gasto[]> {
    try {
      return await this.getRepo().getAllForActiveEmpresa();
    } catch (error) {
      console.error('GastoService.getAll error:', error);
      throw error;
    }
  }

  /**
   * Obtiene un gasto por ID
   */
  static async getById(id: string): Promise<Gasto | undefined> {
    try {
      return await this.getRepo().get(id);
    } catch (error) {
      console.error('GastoService.getById error:', error);
      throw error;
    }
  }

  /**
   * Actualiza un gasto
   */
  static async update(id: string, updates: Partial<Gasto>): Promise<void> {
    try {
      const existente = await this.getById(id);
      if (!existente) {
        throw new Error('Gasto no encontrado');
      }

      await this.getRepo().update({ ...updates, id });
    } catch (error) {
      console.error('Error actualizando gasto:', error);
      throw error;
    }
  }

  /**
   * Elimina un gasto (soft delete)
   */
  static async delete(id: string): Promise<void> {
    try {
      const gasto = await this.getById(id);
      if (!gasto) {
        throw new Error('Gasto no encontrado');
      }
      await this.getRepo().softDelete(id);
    } catch (error) {
      console.error('Error eliminando gasto:', error);
      throw error;
    }
  }

  /**
   * Obtiene gastos por concepto
   */
  static async getByConcepto(concepto: string): Promise<Gasto[]> {
    return await this.getRepo().searchByConcepto(concepto);
  }

  /**
   * Obtiene gastos por categoría
   */
  static async getByCategoria(categoria: string): Promise<Gasto[]> {
    return await this.getRepo().getByCategoria(categoria);
  }

  /**
   * Obtiene gastos por ubicación
   */
  static async getByUbicacion(ubicacionId: string): Promise<Gasto[]> {
    return await this.getRepo().getByUbicacion(ubicacionId);
  }

  /**
   * Obtiene gastos por período
   */
  static async getByPeriodo(
    fechaInicio: string,
    fechaFin: string
  ): Promise<Gasto[]> {
    try {
      return await this.getRepo().getByPeriodo(fechaInicio, fechaFin);
    } catch (error) {
      console.error('GastoService.getByPeriodo error:', error);
      throw error;
    }
  }

  /**
   * Obtiene gastos por año
   */
  static async obtenerGastosPorAnio(anio: number): Promise<Gasto[]> {
    const fechaInicio = `${anio}-01-01`;
    const fechaFin = `${anio}-12-31`;
    return await this.getByPeriodo(fechaInicio, fechaFin);
  }

  /**
   * Obtiene gastos con filtros
   */
  static async getByFiltros(filtros: FiltroGastos): Promise<Gasto[]> {
    return await this.getRepo().getByFiltros(filtros);
  }

  /**
   * Obtiene estadísticas de gastos para un período
   */
  static async getEstadisticas(
    fechaInicio: string,
    fechaFin: string
  ): Promise<{
    total: number;
    promedio: number;
    cantidad: number;
    conceptoMasUsado: string | null;
    montoMaximo: number;
    montoMinimo: number;
  }> {
    const gastos = await this.getByPeriodo(fechaInicio, fechaFin);

    if (gastos.length === 0) {
      return {
        total: 0,
        promedio: 0,
        cantidad: 0,
        conceptoMasUsado: null,
        montoMaximo: 0,
        montoMinimo: 0
      };
    }

    const total = gastos.reduce((sum, g) => sum + g.monto, 0);
    const promedio = total / gastos.length;

    // Concepto más usado
    const conceptos = new Map<string, number>();
    gastos.forEach(g => {
      conceptos.set(g.concepto, (conceptos.get(g.concepto) || 0) + 1);
    });
    const conceptoMasUsado = Array.from(conceptos.entries())
      .sort((a, b) => b[1] - a[1])[0]?.[0] || null;

    const montos = gastos.map(g => g.monto);
    const montoMaximo = Math.max(...montos);
    const montoMinimo = Math.min(...montos);

    return {
      total,
      promedio,
      cantidad: gastos.length,
      conceptoMasUsado,
      montoMaximo,
      montoMinimo
    };
  }

  /**
   * Obtiene los top N conceptos por monto
   */
  static async getTopConceptos(limit: number = 5): Promise<Array<{
    concepto: string;
    total: number;
    cantidad: number;
  }>> {
    const gastos = await this.getAll();
    const conceptos = new Map<string, { total: number; cantidad: number }>();

    gastos.forEach(g => {
      const actual = conceptos.get(g.concepto) || { total: 0, cantidad: 0 };
      conceptos.set(g.concepto, {
        total: actual.total + g.monto,
        cantidad: actual.cantidad + 1
      });
    });

    return Array.from(conceptos.entries())
      .map(([concepto, data]) => ({ concepto, ...data }))
      .sort((a, b) => b.total - a.total)
      .slice(0, limit);
  }
}

export default GastoService;